import styled, {keyframes} from 'styled-components'
import {useParams} from 'react-router-dom'
import {Button, Card} from 'react-bootstrap'
import {BsChevronRight as ChevronIcon} from 'react-icons/bs'
import toast from 'react-hot-toast'
import React, {ReactNode, useEffect, useRef, useState} from 'react'
import useLibrary from '../useLibrary'
import useLocationRegion from '../useLocationRegion'
import RouteContainer from '../../../common/styles/RouteContainer'
import utils from '../../../common/utils'
import RowSpacer from '../../../common/styles/RowSpacer'
import RouteTitle from '../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import RouteTitleConfig from '../../../config/RouteTitleConfig'
import useHtmlTitle from '../../../common/useHtmlTitle'
import breakpoints from '../../../config/Breakpoints'
import parse from 'html-react-parser'
import ColorConfig from "../../../config/ColorConfig";
import LibraryDataAttribution from './LibraryDataAttribution'
import useLoginStore from '../../login/useLoginStore'
import {BooksitoutServer} from '../../../config/BooksitoutServer'
import LibraryMembershipResponse from '../membership/LibraryMembershipResponse'

const LibraryDetailRoute = () => {
    const {libraryId} = useParams()
    const library = useLibrary(parseInt(libraryId ?? ''))
    const locationInfo = useLocationRegion(library?.location.name.regionEnglishName, library?.location.name.regionDetailEnglishName)
    const mapElement = useRef(null)
    useHtmlTitle(library?.name ?? '도서관')
    const membershipId = useMembershipIdForLibrary(library?.id)

    useEffect(() => {
        if (library != null) {
            const {naver} = window
            if (!mapElement.current || !naver) return undefined

            const mapOptions: naver.maps.MapOptions = {
                center: new naver.maps.LatLng(library.location.latitude, library.location.longitude),
                zoom: 16,
                maxZoom: 18,
                minZoom: 13,
                zoomControl: false,
                logoControl: false,
                mapDataControl: false,
                mapTypeControl: false,
                scaleControl: false,
            }

            const map = new naver.maps.Map(mapElement.current, mapOptions)

            // Custom marker with icon
            new naver.maps.Marker({
                position: new naver.maps.LatLng(library.location.latitude, library.location.longitude),
                map,
                icon: {
                    content: `<div style="
						width: 40px;
						height: 40px;
						background: ${ColorConfig.Primary};
						border-radius: 50% 50% 50% 0;
						transform: rotate(-45deg);
						border: 3px solid white;
						box-shadow: 0 4px 8px rgba(0,0,0,0.3);
					"></div>`,
                    size: new naver.maps.Size(40, 40),
                    anchor: new naver.maps.Point(20, 40),
                },
            })

            // 넓은 화면에서는 지도 높이가 왼쪽 열에 맞춰 바뀌므로 크기가 바뀔 때 다시 그림
            const center = new naver.maps.LatLng(library.location.latitude, library.location.longitude)
            const resizeObserver = new ResizeObserver(() => {
                map.autoResize()
                map.setCenter(center)
            })
            resizeObserver.observe(mapElement.current)

            return () => resizeObserver.disconnect()
        }

        return undefined
    }, [library])

    const copyText = (text: string) => {
        const tempTextarea = document.createElement('textarea')
        tempTextarea.value = text
        document.body.appendChild(tempTextarea)

        tempTextarea.select()
        document.execCommand('copy')

        document.body.removeChild(tempTextarea)

        toast.success('주소를 복사했어요')
    }

    const share = async () => {
        try {
            await navigator.share({title: document.title, url: window.location.href})
        } catch (err) {
            if (String(err?.name) === 'AbortError') return

            try {
                await navigator.clipboard.writeText(window.location.href)
                toast.success('주소를 복사했어요')
            } catch {
                toast.error('주소를 복사하지 못했어요')
            }
        }
    }

    const routeTitle = (
        <RouteTitle icon={<booksitoutIcon.library/>} title={'도서관 찾기'} subTitle={'여러 조건으로 도서관을 찾을 수 있어요'}
                    currentKey={'library'} buttons={RouteTitleConfig.Library}/>
    )

    if (library == null) {
        return (
            <RouteContainer>
                {routeTitle}
                <LibraryDetailSkeleton/>
            </RouteContainer>
        )
    }

    const loanText = [
        library.loanBookLimit != null ? `1인 ${library.loanBookLimit}권` : null,
        library.loanDayLimit != null ? `${library.loanDayLimit}일` : null,
    ].filter(Boolean).join(' · ')

    const regionPath = `/library/by-region/${library.location.name.regionEnglishName.toLowerCase()}`
    const hasLocation = library.location.latitude && library.location.longitude

    return (
        <RouteContainer>
            {routeTitle}

            <RowSpacer size={30}/>

            <Header>
                {library.location.logo && <HeaderLogo src={library.location.logo} alt=""/>}
                <LibraryName>{library.name}</LibraryName>

                <HeaderActions>
                    <Button variant="book" href={membershipId != null ? `/library/membership/${membershipId}` : '/add/membership'}>
                        <booksitoutIcon.membership/> {membershipId != null ? '내 회원증' : '회원증 추가'}
                    </Button>
                    <Button variant="book" onClick={share}>
                        <booksitoutIcon.share/> 공유
                    </Button>
                </HeaderActions>
            </Header>

            <RowSpacer size={40}/>

            <Columns>
                <Column>
                    <Section>
                        <InfoRow label="주소" value={library.location.address} onClick={() => copyText(library.location.address)}/>
                        {library.buildingAreaInPyung != null && <InfoRow label="면적" value={`${utils.insertCommas(library.buildingAreaInPyung)}평`}/>}
                        {library.seatCount != null && <InfoRow label="열람석" value={`${utils.insertCommas(library.seatCount)}석`}/>}
                        {library.phone && library.phone !== '?' && <InfoRow label="전화번호" value={library.phone} href={`tel:${library.phone}`}/>}
                        {library.homepage && library.homepage !== '?' && (
                            <InfoRow label="홈페이지" value={library.homepage.substring(library.homepage.indexOf('://') + 3)} href={library.homepage} external/>
                        )}
                    </Section>

                    <Section title="도서">
                        <InfoRow label="보유 도서" value={library.bookCount ? `${utils.insertCommas(library.bookCount)}권` : '-'}/>
                        {loanText && <InfoRow label="대출" value={loanText}/>}
                    </Section>

                    <Section
                        title="운영 시간"
                        right={library.isOpenNow != null && (
                            <OpenBadge isOpen={library.isOpenNow}>
                                {library.isOpenNow
                                    ? `영업 중${library.operatingMonthType === 'SUMMER' ? ' (☀️ 여름 기준)' : library.operatingMonthType === 'WINTER' ? ' (❄️ 겨울 기준)' : ''}`
                                    : <>영업 종료{library.closedReason && <> · {parse(library.closedReason)}</>}</>}
                            </OpenBadge>
                        )}
                    >
                        {Object.entries(library.operatingTimeRanges).map(([period, time]) => (
                            <InfoRow key={period} label={period} value={time}/>
                        ))}
                        {library.restDayDescription && <InfoRow label="휴관일" value={library.restDayDescription}/>}
                    </Section>
                </Column>

                <Column>
                    {hasLocation && (
                        <Section title="지도" grow>
                            <Map ref={mapElement}/>
                            <InfoRow label="네이버 지도에서 열기" href={`https://map.naver.com/p/search/${encodeURIComponent(library.name)}`} external/>
                        </Section>
                    )}

                    {locationInfo?.depth1 && (
                        <Section title="지역">
                            <InfoRow label="시/도" value={<RegionValue name={locationInfo.depth1.koreanName} logo={locationInfo.depth1.logo}/>} href={regionPath}/>
                            {locationInfo.depth2 && library.location.name.regionDetailEnglishName && (
                                <InfoRow
                                    label="구/군/시"
                                    value={<RegionValue name={locationInfo.depth2.koreanName} logo={locationInfo.depth2.logo}/>}
                                    href={`${regionPath}/${library.location.name.regionDetailEnglishName.toLowerCase()}`}
                                />
                            )}
                        </Section>
                    )}
                </Column>
            </Columns>

            <RowSpacer/>

            <LibraryDataAttribution/>
        </RouteContainer>
    )
}

// 이 도서관에서 쓸 수 있는 내 회원증 (로그인했을 때만)
const useMembershipIdForLibrary = (libraryId: number | undefined) => {
    const isLoggedIn = useLoginStore((state) => state.isLoggedIn)()
    const [membershipId, setMembershipId] = useState<number | null>(null)

    useEffect(() => {
        if (libraryId == null || !isLoggedIn) return

        BooksitoutServer
            .get<LibraryMembershipResponse[]>('/v1/library/membership')
            .then((res) => {
                const membership = res.data.find((m) => m.usableLibrary?.some((usable) => usable.id === libraryId))
                setMembershipId(membership?.id ?? null)
            })
            .catch(() => setMembershipId(null))
    }, [libraryId, isLoggedIn])

    return membershipId
}

const Section: React.FC<{ title?: string, right?: ReactNode, grow?: boolean, children: ReactNode }> = ({title, right, grow, children}) => {
    return (
        <SectionCard grow={grow}>
            {title && (
                <SectionHeader>
                    <SectionTitle>{title}</SectionTitle>
                    {right}
                </SectionHeader>
            )}
            {children}
        </SectionCard>
    )
}

const InfoRow: React.FC<{
    label: string
    value?: ReactNode
    href?: string
    external?: boolean
    onClick?: () => void
}> = ({label, value, href, external, onClick}) => {
    const content = (
        <>
            <RowLabel>{label}</RowLabel>
            <RowValue>{value}</RowValue>
            {href && <RowChevron><ChevronIcon/></RowChevron>}
        </>
    )

    if (href) {
        return (
            <RowLink href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
                {content}
            </RowLink>
        )
    }

    return <RowContainer clickable={onClick != null} onClick={onClick}>{content}</RowContainer>
}

const RegionValue = ({name, logo}: { name: string, logo: string }) => {
    return (
        <RegionValueContainer>
            {logo && <RegionLogo src={logo} alt=""/>}
            {name}
        </RegionValueContainer>
    )
}

const LibraryDetailSkeleton = () => {
    return (
        <>
            <RowSpacer size={30}/>

            <Header>
                <SkeletonBar width={80} height={80} radius={12}/>
                <SkeletonBar width={220} height={34}/>
            </Header>

            <RowSpacer size={40}/>

            <Columns>
                <Column>
                    <SkeletonSection count={5}/>
                    <SkeletonSection title="도서" count={2}/>
                    <SkeletonSection title="운영 시간" count={4}/>
                </Column>

                <Column>
                    <SectionCard grow>
                        <SectionHeader><SectionTitle>지도</SectionTitle></SectionHeader>
                        <MapSkeleton/>
                    </SectionCard>
                    <SkeletonSection title="지역" count={2}/>
                </Column>
            </Columns>
        </>
    )
}

const SkeletonSection = ({title, count}: { title?: string, count: number }) => {
    return (
        <SectionCard>
            {title && <SectionHeader><SectionTitle>{title}</SectionTitle></SectionHeader>}
            {Array.from({length: count}).map((_, i) => (
                <RowContainer key={i} clickable={false}>
                    <SkeletonBar width={70} height={16}/>
                    <RowValue><SkeletonBar width={140} height={16}/></RowValue>
                </RowContainer>
            ))}
        </SectionCard>
    )
}

const skeletonGlow = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
`

const SkeletonBar = styled.div<{ width: number, height: number, radius?: number }>`
    width: ${props => props.width}px;
    max-width: 100%;
    height: ${props => props.height}px;
    border-radius: ${props => props.radius ?? 4}px;
    background-color: var(--card-card-bg-color);
    animation: ${skeletonGlow} 1.5s infinite;
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        gap: 20px;
        min-height: 80px;
        text-align: left;
    }
`

const Columns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    column-gap: 24px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;

    .btn {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        margin-left: auto;
    }
`

const HeaderLogo = styled.img`
    width: 80px;
    border-radius: 12px;
`

const LibraryName = styled.h1`
    font-size: 1.8rem;
    font-weight: bold;
    color: var(--text-color);
    margin: 0;
`

const SectionCard = styled(Card)<{ grow?: boolean }>`
    @media screen and (min-width: ${breakpoints.lg}) {
        flex-grow: ${props => props.grow ? 1 : 0};
    }

    margin-bottom: 24px;
    padding: 8px 0;
    border-radius: 16px;
    overflow: hidden;
    background-color: var(--card-bg-color);
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
    color: var(--text-color);
`

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px 4px;
`

const SectionTitle = styled.h3`
    font-size: 1.15rem;
    font-weight: bold;
    color: var(--text-color);
    margin: 0;
`

const OpenBadge = styled.span<{ isOpen: boolean }>`
    padding: 2px 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: bold;
    color: ${props => props.isOpen ? '#2e7d32' : '#c62828'};
    background-color: ${props => props.isOpen ? 'rgba(46, 125, 50, 0.12)' : 'rgba(198, 40, 40, 0.12)'};

    a {
        color: inherit;
        text-decoration: underline;
    }
`

const rowStyle = `
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 20px;
`

const RowContainer = styled.div<{ clickable: boolean }>`
    ${rowStyle}
    cursor: ${props => props.clickable ? 'pointer' : 'default'};

    &:hover {
        background-color: ${props => props.clickable ? 'var(--card-card-bg-color)' : 'transparent'};
    }
`

const RowLink = styled.a`
    ${rowStyle}
    color: inherit;
    text-decoration: none;

    &:hover {
        color: inherit;
        background-color: var(--card-card-bg-color);
    }
`

const RowLabel = styled.span`
    flex-shrink: 0;
    color: var(--text-color);
`

const RowValue = styled.span`
    display: flex;
    justify-content: flex-end;
    flex: 1;
    min-width: 0;
    text-align: right;
    color: var(--sub-text-color);
    overflow-wrap: anywhere;
`

const RowChevron = styled.span`
    display: flex;
    color: var(--sub-text-color);
`

const RegionValueContainer = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
`

const RegionLogo = styled.img`
    width: 24px;
    border-radius: 4px;
`

const Map = styled.div`
    margin: 20px;
    height: 300px;
    border-radius: 12px;
    overflow: hidden;
    z-index: 1;

    @media screen and (min-width: ${breakpoints.lg}) {
        flex: 1;
        height: auto;
        min-height: 200px;
    }
`

const MapSkeleton = styled(Map)`
    background-color: var(--card-card-bg-color);
    animation: ${skeletonGlow} 1.5s infinite;
`

export default LibraryDetailRoute
